import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Card, CardBody, CardHeader, Col } from "reactstrap";
import Swal from "sweetalert2";
import { fetchMemberCards, sendMemberCards } from "../../slices/admin/thunk";

export default function MemberCards({ user }) {
    const dispatch = useDispatch();

    const [cards, setCards] = useState(null);        // { regular, plus }
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let active = true;
        const run = async () => {
            if (!user?.id) return;
            setLoading(true);
            try {
                const data = await dispatch(fetchMemberCards({ userId: user.id })).unwrap();
                if (active) setCards(data || null);
            } catch (e) {
                Swal.fire({ icon: "error", title: "Error", text: e?.message || "Failed to fetch cards" });
            } finally {
                if (active) setLoading(false);
            }
        };
        run();
        return () => { active = false; };
    }, [dispatch, user?.id]);

    const regular = cards?.regular || null;
    const plus = cards?.plus || null;
    const hasRegular = !!regular;
    const hasPlus = !!plus && user?.baxrxPlus === "active";

    const Field = ({ label, value }) => (
        <div className="d-flex justify-content-between align-items-center py-1">
            <span className="text-muted" style={{ fontSize: 12 }}>{label}</span>
            <strong style={{ fontSize: 13 }}>{value || "—"}</strong>
        </div>
    );

    const CardBlock = ({ title, card }) => (
        <div className="p-3 border rounded mb-2">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">{title}</h6>
                <span className="badge bg-light text-dark">
                    {card?.cardHolderName || user?.firstName || "Member"}
                </span>
            </div>
            <Field
                label="Member ID"
                value={`${card?.memberPrefix ? card.memberPrefix : ""}${card?.memberId != null ? String(card.memberId) : ""}`}
            />
            <Field label="BIN" value={card?.bin} />
            <Field label="PCN" value={card?.pcn} />
            <Field label="Group" value={card?.groupId} />
            <Field label="Person Code" value={card?.personCode} />
        </div>
    );

    // --- NEW: prompt for email before sending
    const promptEmail = async () => {
        const { value: email, isConfirmed } = await Swal.fire({
            title: "Send to email",
            input: "email",
            inputValue: user?.email || "",
            inputLabel: "Recipient email",
            inputPlaceholder: "name@example.com",
            showCancelButton: true,
            confirmButtonText: "Send",
            cancelButtonText: "Cancel",
            inputValidator: (val) => {
                if (!val) return "Please enter an email.";
                const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
                return ok ? undefined : "Please enter a valid email address.";
            },
        });
        if (!isConfirmed) return null; // cancelled
        return email?.trim() || null;
    };

    const send = async (opts) => {
        const email = await promptEmail();
        if (!email) return;

        setSending(true);
        try {
            await dispatch(
                sendMemberCards({
                    userId: user.id,
                    email,
                    regular: !!opts.regular,
                    plus: !!opts.plus,
                })
            ).unwrap();

            Swal.fire({
                icon: "success",
                title: "Sent",
                text: `Card${opts.regular && opts.plus ? "s" : ""} emailed to ${email}`,
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (e) {
            Swal.fire({ icon: "error", title: "Error", text: e?.message || "Failed to send cards" });
        } finally {
            setSending(false);
        }
    };

    return (
        <Col xl={4}>
            <Card className="border">
                <CardHeader style={{ background: "var(--thead-bg)" }}>
                    <h5 className="mb-0">Member Cards</h5>
                </CardHeader>
                <CardBody>
                    <p className="text-muted mb-3">Send member cards via email</p>

                    {loading ? (
                        <div className="mb-3">Loading cards…</div>
                    ) : (
                        <>
                            {hasRegular && <CardBlock title="Regular" card={regular} />}
                            {hasPlus && <CardBlock title="BaxRx+" card={plus} />}
                            {!hasRegular && !hasPlus && (
                                <div className="text-muted small mb-2">No card data found.</div>
                            )}
                        </>
                    )}

                    <div className="d-flex flex-column gap-2 mt-2">
                        <Button
                            color="outline-primary"
                            size="sm"
                            onClick={() => send({ regular: true, plus: false })}
                            disabled={!hasRegular || sending}
                            className="d-flex align-items-center justify-content-center"
                        >
                            <i className="ri-mail-send-line me-2"></i>
                            Send Regular Card
                        </Button>

                        {user.baxrxPlus === "active" && (
                            <Button
                                color="outline-success"
                                size="sm"
                                onClick={() => send({ regular: false, plus: true })}
                                disabled={!hasPlus || sending}
                                className="d-flex align-items-center justify-content-center"
                            >
                                <i className="ri-mail-send-line me-2"></i>
                                Send BaxRx+ Card
                            </Button>
                        )}

                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => send({ regular: hasRegular, plus: hasPlus })}
                            disabled={(!hasRegular && !hasPlus) || sending}
                            className="d-flex align-items-center justify-content-center"
                        >
                            <i className="ri-mail-send-line me-2"></i>
                            Send Both Cards
                        </Button>

                        {sending && <div className="text-muted small">Sending…</div>}
                    </div>
                </CardBody>
            </Card>
        </Col>
    );
}